import { useEffect, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';

export type CatalogItem = components['schemas']['CatalogItemResponse'];
export type CatalogCategory = components['schemas']['CatalogCategoryResponse'];
export type CatalogItemType = components['schemas']['CatalogItemType'];
export type SaveCatalogItemRequest = components['schemas']['SaveCatalogItemRequest'];
export type PriceAdjustmentRequest = components['schemas']['PriceAdjustmentRequest'];
export type PriceAdjustmentLine = components['schemas']['PriceAdjustmentLine'];
export type PriceAdjustmentMode = components['schemas']['PriceAdjustmentMode'];
export type PriceAdjustmentDirection = components['schemas']['PriceAdjustmentDirection'];

/**
 * `satisfies` pins each entry to a real member of the generated union — the
 * `VEHICLE_TYPES` trick, with its caveat: a member ADDED on the API side still
 * compiles here and simply never appears in the picker.
 */
export const CATALOG_ITEM_TYPES = [
  'Service',
  'Part',
  // M2-33: priced per hour; time entries bill as one.
  'Labor',
] as const satisfies readonly CatalogItemType[];

/**
 * The API's upper bound on a price or a cost (`Money.Max`, CC-21), restated so
 * the form can refuse it with a sentence before the request is sent.
 */
export const MAX_AMOUNT = 99_999.99;

/** What the list filters on. `category` is an id, `'uncategorized'`, or `''` for any. */
export interface CatalogFilters {
  search: string;
  type: '' | CatalogItemType;
  category: string;
  includeArchived: boolean;
  page: number;
}

export const UNCATEGORIZED = 'uncategorized';

/** Page size for the price book. The API's cap is 100; 50 fills a screen without scrolling forever. */
export const CATALOG_PAGE_SIZE = 50;

const SEARCH_DEBOUNCE_MS = 250;

const ITEMS_KEY = ['catalog-items'] as const;
const CATEGORIES_KEY = ['catalog-categories'] as const;

/** Thrown for a 400 the API explains, carrying its own wording per field. */
export class CatalogValidationError extends Error {
  constructor(public readonly errors: Record<string, string[]>) {
    super(Object.values(errors).flat()[0] ?? 'Invalid');
    this.name = 'CatalogValidationError';
  }

  /** The server's sentence for one field, matched case-insensitively — ASP.NET's keys are PascalCase. */
  forField(field: string): string | undefined {
    const key = Object.keys(this.errors).find((k) => k.toLowerCase() === field.toLowerCase());
    return key ? this.errors[key]?.[0] : undefined;
  }
}

/**
 * Thrown for a 409 on saving an item (M2-33 review): a labor item that unbilled
 * time still names keeps its kind, or another item became the standard labor
 * rate at the same moment. Carries the server's sentence for the form to show.
 */
export class CatalogConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CatalogConflictError';
  }
}

/**
 * Thrown for the 409 an apply gets when the book changed after the preview.
 * Carries the server's sentence; the dialog shows it and asks for a new
 * preview rather than retrying on its own.
 */
export class PriceBookChangedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PriceBookChangedError';
  }
}

/**
 * ⚠️ Read from openapi-fetch's `error`, never by re-reading the `Response` —
 * its body has already been consumed (useLeadSources records the same trap).
 */
function validationError(error: unknown): CatalogValidationError {
  const problem = error as { errors?: Record<string, string[]> } | undefined;
  return new CatalogValidationError(problem?.errors ?? {});
}

/**
 * A page of the price book (M2-02), filtered on the server.
 *
 * The search is debounced here rather than in the screen, so typing
 * "water heater" is one request rather than twelve; the other filters apply
 * at once. `keepPreviousData` keeps the table on screen while the next page
 * loads instead of flashing a spinner on every keystroke.
 */
export function useCatalogItems(filters: CatalogFilters) {
  const [search, setSearch] = useState(filters.search);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(filters.search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filters.search]);

  const query = {
    q: search.trim() || undefined,
    type: filters.type || undefined,
    categoryId:
      filters.category && filters.category !== UNCATEGORIZED ? filters.category : undefined,
    uncategorized: filters.category === UNCATEGORIZED ? true : undefined,
    includeArchived: filters.includeArchived || undefined,
    page: filters.page,
    pageSize: CATALOG_PAGE_SIZE,
  };

  return useQuery({
    queryKey: [...ITEMS_KEY, query] as const,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/catalog-items', {
        params: { query },
      });
      if (!response.ok || !data) {
        throw new Error(`GET /catalog-items failed: ${response.status}`);
      }
      return {
        items: data.items,
        // int32 widens to `number | string` in the generated client.
        totalCount: Number(data.totalCount),
        page: Number(data.page),
        pageSize: Number(data.pageSize),
      };
    },
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/**
 * The account's categories. `includeRetired` is for the management screen and
 * for naming the category of an item filed under a retired one; every picker
 * wants active ones only.
 */
export function useCatalogCategories(includeRetired = false) {
  return useQuery({
    queryKey: [...CATEGORIES_KEY, { includeRetired }] as const,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/catalog-categories', {
        params: { query: { includeRetired } },
      });
      if (!response.ok || !data) {
        throw new Error(`GET /catalog-categories failed: ${response.status}`);
      }
      return [...data].sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder));
    },
    retry: false,
  });
}

/**
 * ⚠️ A category edit invalidates the ITEMS too: every item carries its
 * category's label and retired flag, so a rename that refreshed only the
 * category list would leave the old name on every row of the price book —
 * the stale-label bug M1-38 recorded for skills.
 */
function invalidatePricebook(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ITEMS_KEY });
  void queryClient.invalidateQueries({ queryKey: CATEGORIES_KEY });
}

export function useSaveCatalogItem(id?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SaveCatalogItemRequest) => {
      const { data, error, response } =
        id === undefined
          ? await apiClient.POST('/api/v1/catalog-items', { body })
          : await apiClient.PUT('/api/v1/catalog-items/{id}', { params: { path: { id } }, body });
      if (response.status === 400) {
        throw validationError(error);
      }
      if (response.status === 409) {
        throw new CatalogConflictError(
          typeof error === 'string' ? error : `save catalog item failed: ${response.status}`,
        );
      }
      if (!response.ok || !data) {
        throw new Error(`save catalog item failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => invalidatePricebook(queryClient),
  });
}

export function useSetCatalogItemArchived() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, archived }: { id: string; archived: boolean }) => {
      const path = archived
        ? '/api/v1/catalog-items/{id}/archive'
        : '/api/v1/catalog-items/{id}/restore';
      const { data, response } = await apiClient.POST(path, { params: { path: { id } } });
      if (!response.ok || !data) {
        throw new Error(`POST ${path} failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => invalidatePricebook(queryClient),
  });
}

export function useCreateCatalogCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (label: string) => {
      const { data, error, response } = await apiClient.POST('/api/v1/catalog-categories', {
        body: { label },
      });
      if (response.status === 400) {
        throw validationError(error);
      }
      if (!response.ok || !data) {
        throw new Error(`POST /catalog-categories failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => invalidatePricebook(queryClient),
  });
}

export function useUpdateCatalogCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      label,
      sortOrder,
    }: {
      id: string;
      label: string;
      sortOrder: number;
    }) => {
      const { data, error, response } = await apiClient.PUT('/api/v1/catalog-categories/{id}', {
        params: { path: { id } },
        body: { label, sortOrder },
      });
      if (response.status === 400) {
        throw validationError(error);
      }
      if (!response.ok || !data) {
        throw new Error(`PUT /catalog-categories/{id} failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => invalidatePricebook(queryClient),
  });
}

export function useSetCatalogCategoryRetired() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, retired }: { id: string; retired: boolean }) => {
      const path = retired
        ? '/api/v1/catalog-categories/{id}/retire'
        : '/api/v1/catalog-categories/{id}/restore';
      const { data, error, response } = await apiClient.POST(path, { params: { path: { id } } });
      // Restore can be refused: an active category may have taken the label.
      if (response.status === 400) {
        throw validationError(error);
      }
      if (!response.ok || !data) {
        throw new Error(`POST ${path} failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => invalidatePricebook(queryClient),
  });
}

/** What an adjustment would do. Writes nothing, so it invalidates nothing. */
export function usePreviewPriceAdjustment() {
  return useMutation({
    mutationFn: async (body: PriceAdjustmentRequest) => {
      const { data, error, response } = await apiClient.POST(
        '/api/v1/catalog-items/price-adjustments/preview',
        { body },
      );
      if (response.status === 400) {
        throw validationError(error);
      }
      if (!response.ok || !data) {
        throw new Error(`preview price adjustment failed: ${response.status}`);
      }
      return data.lines;
    },
  });
}

/**
 * Applies exactly the previewed adjustment: the preview's own lines go back as
 * `expected`, and the API refuses (409) if the book no longer matches them.
 */
export function useApplyPriceAdjustment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      adjustment,
      preview,
    }: {
      adjustment: PriceAdjustmentRequest;
      preview: PriceAdjustmentLine[];
    }) => {
      const { data, error, response } = await apiClient.POST(
        '/api/v1/catalog-items/price-adjustments',
        {
          body: {
            ...adjustment,
            expected: preview.map((line) => ({ id: line.id, defaultPrice: line.oldPrice })),
          },
        },
      );
      if (response.status === 409) {
        throw new PriceBookChangedError(
          typeof error === 'string' ? error : 'The price book changed since the preview.',
        );
      }
      if (response.status === 400) {
        throw validationError(error);
      }
      if (!response.ok || !data) {
        throw new Error(`apply price adjustment failed: ${response.status}`);
      }
      return data.lines;
    },
    onSuccess: () => invalidatePricebook(queryClient),
  });
}

// --- Labor rate tiers (M2-33) ------------------------------------------------------

export type LaborRateTiers = components['schemas']['LaborRateTiersResponse'];

const TIERS_KEY = ['labor-rate-tiers'] as const;

/** The account's Overtime and Emergency multipliers (Standard is always 1×). Read on `customers.read`. */
export function useLaborRateTiers() {
  return useQuery({
    queryKey: TIERS_KEY,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/account/labor-rate-tiers');
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/account/labor-rate-tiers failed: ${response.status}`);
      }
      return data;
    },
  });
}

/** Sets the multipliers (`pricebook.manage`). A 400 carries the API's sentence per field. */
export function useSaveLaborRateTiers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { overtimeMultiplier: number; emergencyMultiplier: number }) => {
      const { data, error, response } = await apiClient.PUT('/api/v1/account/labor-rate-tiers', {
        body,
      });
      if (response.status === 400) {
        throw validationError(error);
      }
      if (!response.ok || !data) {
        throw new Error(`PUT /api/v1/account/labor-rate-tiers failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: (saved) => queryClient.setQueryData(TIERS_KEY, saved),
  });
}
